import { HttpClient } from '@angular/common/http';
import { Data, Response, UPSERT, waitAndRetry } from '@dimandzhi/graphql';
import { currentMethodName } from 'app/current-method-name';
import { GraphQL } from 'app/graphql';
import { environment as env } from 'environments/environment';
import { log } from 'environments/log';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

type gqlType = GraphQL.Group;
const gqlType = GraphQL.Group;
const ModelName = gqlType.getModelName();
export class Groups {
  private groupsFirstCall: boolean;
  private groups = new BehaviorSubject<gqlType[]>([]);

  constructor(
    protected http: HttpClient
  ) { }

  initGroups() {
    this.groupsFirstCall = true;
    this.groups = new BehaviorSubject<gqlType[]>([]);
  }

  setGroup(group: gqlType) {
    let action: UPSERT = 'update';
    if (group.id === undefined) {
      action = 'insert';
    }
    return this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      mutation {
        ${action} {
          ${gqlType.getModelName()}(values:[{${group.mutationData()}}])
        }
      }`
    ).pipe(
      first(),
      Data.fromResp,
      map(data => {
        const result = data.upsertResp(action, ModelName);
        if (result === undefined) {
          throw new Error(`result is undefined`);
        }
        return result;
      })
    );
  }

  insertGroup(group: gqlType) {
    return this.setGroup(group).pipe(
      map(IDs => {
        if (!_.isArrayLike(IDs)) {
          throw new Error(`IDs is not an array: ${typeof IDs} ${IDs}`);
        }
        const id = _.head(IDs);
        if (!_.isNumber(id)) {
          throw new Error(`id is not a number: ${typeof id} ${id}`);
        }
        this.getGroup(id).subscribe({
          next: group => {
            if (_.isNull(group)) {
              log('unexpected: null result');
              this.refreshGroups();
              return;
            }
            this.groups.next(this.groups.value.concat(group));
          },
          error: err => log(err)
        });
        return id;
      })
    );
  }

  updateGroup(group: gqlType) {
    return this.setGroup(group).pipe(
      map(rowsAffected => {
        if (!_.isNumber(rowsAffected)) {
          throw new Error(`rowsAffected is not a number: ${typeof rowsAffected} ${rowsAffected}`);
        }
        if (rowsAffected <= 0) {
          return;
        }
        this.getGroup(group.id).subscribe({
          next: group => {
            if (_.isNull(group)) {
              return;
            }
            const i = _.findIndex(this.groups.value, gr => gr.id === group.id);
            if (i < 0) {
              log('unexpected: item not found in array');
              this.refreshGroups();
              return;
            }
            this.groups.value[i] = group;
            this.groups.next(this.groups.value);
          },
          error: err => log(err)
        });
        return rowsAffected;
      })
    );
  }

  deleteGroup(group: gqlType) {
    return this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      mutation {
        delete {
          ${gqlType.getModelName()}(values:[${group.id}])
        }
      }`
    ).pipe(
      first(),
      Data.fromResp,
      map(data => {
        const rowsAffected = data.deleteResp(ModelName);
        if (!_.isNumber(rowsAffected)) {
          throw new Error(`rowsAffected is undefined: ${typeof rowsAffected} ${rowsAffected}`);
        }
        this.groups.next(this.groups.value.filter(gr => gr.id !== group.id));
        return rowsAffected;
      })
    );
  }

  getGroups(): Observable<gqlType[]>;
  getGroups(asSnapshot: boolean): gqlType[];
  getGroups(asSnapshot?: boolean) {
    if (this.groupsFirstCall) {
      this.refreshGroups();
      this.groupsFirstCall = false;
    }
    if (asSnapshot) {
      return this.groups.getValue();
    }
    return this.groups.asObservable();
  }

  private refreshGroups() {
    this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      query {
        ${gqlType.getModelName()} {
          get {
            data {
              id
              name
              asrPriorityOffset
            }
          }
        }
      }`
    ).pipe(waitAndRetry, Data.fromResp).subscribe({
      next: data => this.groups.next(gqlType.fromData(data)),
      error: err => this.groups.error(err)
    });
  }

  getGroup(id: number) {
    return this.http.post<Response>(`${env.graphql}/${currentMethodName()}`, `
      query {
        ${gqlType.getModelName()} {
          get(id: ${id}) {
            data {
              id
              name
              asrPriorityOffset
            }
          }
        }
      }`
    ).pipe(
      waitAndRetry,
      Data.fromResp,
      map(data => _.head(gqlType.fromData(data)) || null)
    );
  }
}
